/**
 * Two-party preferred (TPP) head-to-head
 * The visualisation will include both won seats and predicted seats and have a line in the centre showing the target to winning the election.
 */

import React, { useMemo } from 'react'
import { getPartyColors, roundToOneDecimalPlace } from '../data'
import { SeatBarGraph } from '../SeatCard/SeatCard'
import {
    SeatVoteCountContainer,
    SeatVotesContainer,
} from '../SeatCard/SeatCard.styled'
import {
    ElectionTppEstCardContentContainer,
    ElectionTppEstCardText,
    ElectionTppEstCardTitle,
    ElectionTppsEstCardErrorContainer,
    ElectionTppsEstCardErrorText,
} from './ElectionTppEstCard.styled'
import {
    ElectionConfig,
    PartyDto,
} from '@west-australian-newspapers/election-api-types'
import { electionsDebug } from '../../../__helpers/elections-debug'

interface ElectionTppEstData {
    leftPartyName?: string
    leftPartyPercentage: number
    leftPartyColor: string
    rightPartyName?: string
    rightPartyColor: string
}

interface ElectionTppEstCardProps {
    title: string
    data: PartyDto[]
    config: ElectionConfig
}

export const ElectionTppEstCard = ({
    title,
    data,
    config,
}: ElectionTppEstCardProps) => {
    const tppData: ElectionTppEstData | false = useMemo(() => {
        if (data.length < 2) {
            electionsDebug('Not enough data to render TPP card.}', data)
            return false
        }

        let partyData: PartyDto[] = []

        /**
         * In WA, we only want to show ALP and LIB, with ALP on the left
         * @todo we should add config item to the Election API, eg `"head2headParties": {"partyCode","partyCode"}`
         * to determine what parties to show
         */
        if (config.electionType === 'state') {
            partyData = data
                .filter(
                    (party) =>
                        party.partyCode === 'ALP' || party.partyCode === 'LIB',
                )
                .sort((a, b) => (a.partyCode === 'ALP' ? -1 : 1))
        } else {
            // Grab the top 2 parties from the incoming data
            // These are sorted by seats won, so the top 2 are always what we want to show
            partyData = data
                .filter(
                    (party) =>
                        party.partyCode === 'ALP' || party.partyCode === 'COA',
                )
                .sort((a, b) => (a.partyCode === 'ALP' ? -1 : 1)) // Always put ALP on the left
        }
        if (partyData.length < 2) {
            electionsDebug('Not enough data to render TPP card.}', data)
            return false
        }

        // Zero data state
        if (
            partyData.length < 2 ||
            partyData[0].votePct === 0 ||
            partyData[1].votePct === 0
        ) {
            return {
                leftPartyName: '',
                leftPartyPercentage: 0,
                leftPartyColor: getPartyColors('NONE').light,
                rightPartyName: '',
                rightPartyColor: getPartyColors('NONE').dark,
                rightPartyPercentage: 100,
            }
        }

        // Calculate the percentage of the left party which is used to calculate the width of the bar
        const leftPartyPercentage = parseFloat(
            (
                (100 * partyData[0].votePct) /
                (partyData[0].votePct + partyData[1].votePct)
            ).toFixed(1),
        )

        // Transform the data into the format required by the component
        return {
            leftPartyName:
                partyData[0].shortPartyName || partyData[0].partyName,
            leftPartyPercentage: leftPartyPercentage,
            leftPartyColor: getPartyColors(partyData[0].partyCode).primary,
            rightPartyName:
                partyData[1].shortPartyName || partyData[1].partyName,
            rightPartyColor: getPartyColors(partyData[1].partyCode).primary,
        }
    }, [data, config])

    if (!tppData) {
        return (
            <ElectionTppEstCardContentContainer>
                <>
                    <ElectionTppEstCardTitle>{title}</ElectionTppEstCardTitle>
                    <ElectionTppsEstCardErrorContainer>
                        <ElectionTppsEstCardErrorText bold>
                            Error
                        </ElectionTppsEstCardErrorText>
                        <ElectionTppsEstCardErrorText>
                            Refresh the page or try again later.
                        </ElectionTppsEstCardErrorText>
                    </ElectionTppsEstCardErrorContainer>
                </>
            </ElectionTppEstCardContentContainer>
        )
    }

    const leftPartyPercentage = roundToOneDecimalPlace(
        tppData.leftPartyPercentage,
    )
    /** If the leftPartyPercentage is 0, this means we're at 0 state so we want both sides to show 0 */
    const rightPartyPercentage =
        tppData.leftPartyPercentage > 0
            ? roundToOneDecimalPlace(100 - tppData.leftPartyPercentage)
            : 0

    return (
        <ElectionTppEstCardContentContainer>
            <>
                <ElectionTppEstCardTitle>{title}</ElectionTppEstCardTitle>
                <div>
                    <SeatVotesContainer>
                        <SeatVoteCountContainer color={tppData.leftPartyColor}>
                            <ElectionTppEstCardText variant="percentage">
                                {`${leftPartyPercentage}%`}
                            </ElectionTppEstCardText>
                            <ElectionTppEstCardText variant="label">
                                {tppData.leftPartyName}
                            </ElectionTppEstCardText>
                        </SeatVoteCountContainer>
                        <SeatVoteCountContainer color={tppData.rightPartyColor}>
                            <ElectionTppEstCardText variant="label">
                                {tppData.rightPartyName}
                            </ElectionTppEstCardText>
                            <ElectionTppEstCardText variant="percentage">
                                {`${rightPartyPercentage}%`}
                            </ElectionTppEstCardText>
                        </SeatVoteCountContainer>
                    </SeatVotesContainer>
                    <SeatBarGraph
                        candidateOneColor={tppData.leftPartyColor}
                        candidateTwoColor={tppData.rightPartyColor}
                        candidateOnePct={leftPartyPercentage}
                        candidateTwoPct={rightPartyPercentage}
                        isTppState={true}
                    />
                </div>
            </>
        </ElectionTppEstCardContentContainer>
    )
}
